'use client';

import { LoadingDark, Typography } from '@musinsa/mds';
import { useCommonLayout } from '@musinsa/mds-ui';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import type {
  CurationSnapDetailItem,
  GetCurationSnapsServiceResponse,
} from '@musinsa/services-curator';
import { useInfiniteQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { curatorQueries } from '../../query-factory';
import { SnapDetailImageBlock } from './SnapDetailImageBlock';
import { SnapTaggedGoodsRow } from './SnapTaggedGoodsRow';

const PAGE_SIZE = 20;

interface Props {
  curationId: string;
  snapId: string;
  shopSlug: string;
}

export const SnapDetailPageView = ({ curationId, snapId, shopSlug }: Props) => {
  const { service, isMobile } = useDeviceInfo();
  const isPC = !isMobile;
  const isMusinsa = service === SERVICE.MUSINSA;

  const query = useInfiniteQuery({
    ...curatorQueries.curations.snapDetail({
      curationId,
      snapId,
      size: PAGE_SIZE,
    }),
    initialPageParam: 1,
    enabled: isMusinsa,
    getNextPageParam: (lastPage: GetCurationSnapsServiceResponse) => {
      if (lastPage.list.length === 0) return undefined;
      return lastPage.pagination.hasNextPage ? lastPage.pagination.nextPage : undefined;
    },
  });

  // NOTE: 스냅 상세 페이지 타이틀 변경을 위해 useCommonLayout 재호출
  const title = query.data?.pages[0]?.title ?? '';
  useCommonLayout({
    containers: ['gnb', 'fab', 'header'],
    options: {
      ...(isPC && isMusinsa && { contentSize: 'max600' }),
      gnb: {
        use: isPC && isMusinsa,
      },
      fab: {
        use: isPC && isMusinsa,
      },
      appbar: {
        use: isMusinsa,
        useBackButton: isMusinsa,
        title: isMusinsa ? title : '',
      },
    },
    isPcEnvironment: isPC,
  });

  const snaps = useMemo(() => query.data?.pages.flatMap((p) => p.list) ?? [], [query.data?.pages]);

  const total = query.data?.pages[0]?.total ?? 0;

  const fetchNextData = useCallback(() => {
    if (query.isFetching || query.isFetchingNextPage || !query.hasNextPage) return;
    void query.fetchNextPage();
  }, [query]);

  if (query.isPending) {
    return (
      <div className="flex flex-1 items-center justify-center p-4">
        <LoadingDark size="size24x24" />
      </div>
    );
  }

  if (query.isError) {
    return null;
  }

  return (
    <section className="pb-9">
      <div className="px-4 py-3">
        <Typography colorScheme="gray-600" variant="body13pxReg">
          스냅 {total}개
        </Typography>
      </div>
      <Virtuoso
        useWindowScroll
        data={snaps}
        endReached={fetchNextData}
        increaseViewportBy={600}
        itemContent={(_, snap: CurationSnapDetailItem) => (
          <div key={snap.id} className={clsx(snap.goods?.length === 0 && 'pb-[32px]!')}>
            <SnapDetailImageBlock imageUrls={snap.imageUrls} isMobile={isMobile} snapId={snap.id} />
            <SnapTaggedGoodsRow
              curationId={curationId}
              goods={snap.goods ?? []}
              pageSize={PAGE_SIZE}
              shopSlug={shopSlug}
              snapId={snapId}
            />
          </div>
        )}
      />
    </section>
  );
};
